/* Bulltong — The Herd · global interactions. Vanilla, deferred, no dependencies. */
(() => {
  'use strict';

  const doc = document;
  const root = doc.documentElement;
  const config = window.Bulltong || { routes: {}, strings: {} };
  const routes = config.routes || {};
  const strings = config.strings || {};
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = matchMedia('(hover: hover) and (pointer: fine)');
  const $ = (selector, scope = doc) => scope.querySelector(selector);
  const $$ = (selector, scope = doc) => Array.from(scope.querySelectorAll(selector));
  const escapeHtml = (value = '') => String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  window.BulltongReady = true;

  function announce(message) {
    const region = $('[data-announcer]');
    if (!region || !message) return;
    region.textContent = '';
    requestAnimationFrame(() => { region.textContent = message; });
  }

  function formatMoney(cents, format = config.moneyFormat || '{{amount}}') {
    const value = Number(cents) || 0;
    const group = (number, precision, thousands = ',', decimal = '.') => {
      const [whole, fraction] = (number / 100).toFixed(precision).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => ({
      amount: group(value, 2),
      amount_no_decimals: group(value, 0),
      amount_with_comma_separator: group(value, 2, '.', ','),
      amount_no_decimals_with_comma_separator: group(value, 0, '.', ','),
      amount_with_apostrophe_separator: group(value, 2, "'", '.'),
      amount_with_space_separator: group(value, 2, ' ', ',')
    }[key] ?? group(value, 2)));
  }

  /* ------------------------------------------------------------------ Header */
  function initHeader() {
    const sentinel = $('[data-scroll-sentinel]');
    if (!sentinel || !('IntersectionObserver' in window)) return;
    new IntersectionObserver(([entry]) => root.classList.toggle('is-scrolled', !entry.isIntersecting)).observe(sentinel);
  }

  /* ------------------------------------------------------------------ Panels */
  const PANEL_OUT_MS = 420;

  function openPanel(dialog, opener) {
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    if (dialog.open) return true;
    $$('dialog.panel[open]').forEach((other) => closePanel(other, true));
    dialog.returnFocusTo = opener || doc.activeElement;
    dialog.showModal();
    root.classList.add('is-locked');
    requestAnimationFrame(() => requestAnimationFrame(() => dialog.classList.add('is-open')));
    const autofocus = $('[data-predictive-search]', dialog);
    if (autofocus) autofocus.focus({ preventScroll: true });
    return true;
  }

  function closePanel(dialog, immediate = false) {
    if (!dialog || !dialog.open || dialog.dataset.closing) return;
    dialog.dataset.closing = 'true';
    dialog.classList.remove('is-open');
    const finish = () => {
      dialog.close();
      delete dialog.dataset.closing;
      if (!$('dialog.panel[open]')) root.classList.remove('is-locked');
      const target = dialog.returnFocusTo;
      if (target && target.isConnected && !immediate) target.focus({ preventScroll: true });
    };
    if (immediate || reducedMotion.matches) finish();
    else setTimeout(finish, PANEL_OUT_MS);
  }

  function initPanels() {
    doc.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-open-dialog]');
      if (opener) {
        if (openPanel(doc.getElementById(opener.dataset.openDialog), opener)) {
          event.preventDefault();
          if (opener.dataset.openDialog === 'cart-dialog') refreshDrawer().then(() => synchronizeCartStock());
        }
        return;
      }
      if (event.target.closest('[data-close-dialog]')) {
        closePanel(event.target.closest('dialog'));
        return;
      }
      if (event.target.matches('dialog.panel')) closePanel(event.target);
      const inPanelLink = event.target.closest('dialog.panel a[href^="#"]');
      if (inPanelLink) closePanel(inPanelLink.closest('dialog'), true);
    });
    doc.addEventListener('cancel', (event) => {
      if (!event.target.matches('dialog.panel')) return;
      event.preventDefault();
      closePanel(event.target);
    }, true);
  }

  /* -------------------------------------------------------------------- Cart */
  const CART_SECTION = 'cart-drawer';
  const inventoryCache = new Map();

  function stockTracked(variant) {
    return variant?.inventoryManagement === 'shopify' && Number.isFinite(Number(variant.inventoryQuantity));
  }

  async function getLiveVariant(handle, variantId, forceRefresh = false) {
    if (!handle || !variantId) return null;
    const cacheKey = `${handle}:${variantId}`;
    if (forceRefresh) inventoryCache.delete(cacheKey);
    const cached = inventoryCache.get(cacheKey);
    if (cached && cached.expires > Date.now()) return cached.variant;
    try {
      const url = new URL(`${routes.root || '/'}products/${encodeURIComponent(handle)}`, location.origin);
      url.searchParams.set('section_id', 'main');
      url.searchParams.set('_stock_check', String(Date.now()));
      const response = await fetch(url, { headers: { Accept: 'text/html' }, cache: 'no-store' });
      if (!response.ok) return null;
      const markup = await response.text();
      const productJson = new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-product-json]');
      if (!productJson) return null;
      const product = JSON.parse(productJson.textContent);
      const variant = product.variants?.find((item) => String(item.id) === String(variantId)) || null;
      if (variant) inventoryCache.set(cacheKey, { variant, expires: Date.now() + 2000 });
      return variant;
    } catch (_) { return null; }
  }

  async function getCartSnapshot() {
    try {
      const url = new URL(`${routes.root || '/'}cart.js`, location.origin);
      url.searchParams.set('_stock_check', String(Date.now()));
      const response = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      return response.ok ? await response.json() : null;
    } catch (_) { return null; }
  }

  async function capAddToLiveStock(form, data) {
    const variant = await getLiveVariant(form.dataset.productHandle, data.get('id'), true);
    if (!stockTracked(variant)) return true;
    const cart = await getCartSnapshot();
    if (!cart) return true; // Shopify's add endpoint remains the final stock check.
    const alreadyInCart = (cart.items || []).filter((item) => String(item.variant_id) === String(variant.id))
      .reduce((total, item) => total + Number(item.quantity || 0), 0);
    const available = Math.max(0, Math.floor(Number(variant.inventoryQuantity)) - alreadyInCart);
    const requested = Math.max(1, parseInt(data.get('quantity'), 10) || 1);
    const status = $('[data-form-status]', form);
    if (available === 0 || variant.available === false) {
      if (status) status.textContent = alreadyInCart ? 'You already have all currently available stock in your bag.' : 'This size is now out of stock. Please choose another size.';
      return false;
    }
    if (requested > available) {
      data.set('quantity', String(available));
      const quantityInput = $('input[name="quantity"]', form);
      if (quantityInput) quantityInput.value = String(available);
      if (status) status.textContent = `Only ${available} more currently available; your quantity has been set to ${available}.`;
    }
    return true;
  }

  async function cartLineStockLimit(line, cart) {
    if (!line) return null;
    const variant = await getLiveVariant(line.dataset.productHandle, line.dataset.variantId, true);
    if (!stockTracked(variant)) return null;
    const sameVariant = (cart?.items || []).filter((item) => String(item.variant_id) === String(variant.id));
    const currentItem = sameVariant.find((item) => item.key === line.dataset.cartKey);
    const currentQuantity = Number(currentItem?.quantity || $('[data-cart-input]', line)?.value || 0);
    const otherQuantity = sameVariant.reduce((total, item) => total + Number(item.quantity || 0), 0) - currentQuantity;
    return Math.max(0, Math.floor(Number(variant.inventoryQuantity)) - otherQuantity);
  }

  let stockSync = null;
  async function synchronizeCartStock() {
    if (stockSync) return stockSync;
    stockSync = (async () => {
      inventoryCache.clear();
      const cart = await getCartSnapshot();
      if (!cart?.items?.length) return false;
      const byVariant = new Map();
      for (const item of cart.items) {
        const group = byVariant.get(String(item.variant_id)) || [];
        group.push(item);
        byVariant.set(String(item.variant_id), group);
      }
      const changes = [];
      for (const items of byVariant.values()) {
        const first = items[0];
        const variant = await getLiveVariant(first.handle, first.variant_id, true);
        if (!stockTracked(variant)) continue;
        let remaining = Math.max(0, Math.floor(Number(variant.inventoryQuantity)));
        for (const item of items) {
          const permitted = Math.min(Number(item.quantity || 0), remaining);
          remaining -= permitted;
          if (permitted < Number(item.quantity || 0)) changes.push({ key: item.key, quantity: permitted });
        }
      }
      if (!changes.length) return false;
      for (const change of changes) {
        const response = await fetch(`${routes.cartChange || '/cart/change'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: change.key, quantity: change.quantity, sections: [CART_SECTION], sections_url: location.pathname })
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || result.status) throw new Error(result.description || result.message || strings.error);
      }
      await refreshDrawer();
      showCartError('Your bag was updated to match current Shopify stock. Please review the quantities.');
      announce('Your bag was updated to match current stock.');
      return true;
    })().catch((error) => {
      showCartError(error.message || strings.error);
      return false;
    }).finally(() => { stockSync = null; });
    return stockSync;
  }

  function updateCartCount(count) {
    $$('[data-cart-count]').forEach((badge) => {
      const changed = badge.textContent.trim() !== String(count);
      badge.textContent = count;
      badge.classList.toggle('is-filled', count > 0);
      if (changed && !reducedMotion.matches) {
        badge.getAnimations().forEach((animation) => animation.cancel());
        badge.animate([
          { transform: 'scale(1)' },
          { transform: 'scale(1.35)', offset: 0.3 },
          { transform: 'scale(1)' }
        ], { duration: 600, easing: 'cubic-bezier(.2,.8,.2,1)' });
      }
    });
  }

  function renderDrawer(sections) {
    const markup = sections && sections[CART_SECTION];
    if (!markup) return false;
    const fresh = new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-cart-drawer]');
    const current = $('[data-cart-drawer]');
    if (!fresh || !current) return false;
    const scroll = $('[data-cart-lines]', current)?.scrollTop || 0;
    const focused = doc.activeElement && current.contains(doc.activeElement) ? doc.activeElement : null;
    const focusKey = focused?.dataset.cartChange || focused?.dataset.cartInput;
    const focusDirection = focused && focused.dataset.quantity !== undefined && focused.closest('.qty') ? (focused.nextElementSibling ? 'minus' : 'plus') : null;
    current.replaceWith(fresh);
    const lines = $('[data-cart-lines]', fresh);
    if (lines) lines.scrollTop = scroll;
    updateCartCount(Number(fresh.dataset.itemCount) || 0);
    if (focused) {
      let target = null;
      if (focusKey) {
        const candidates = $$(`[data-cart-change="${CSS.escape(focusKey)}"], [data-cart-input="${CSS.escape(focusKey)}"]`, fresh);
        target = focusDirection === 'minus' ? candidates[0] : focusDirection === 'plus' ? candidates[2] : candidates.find((el) => el.matches('[data-cart-input]'));
      }
      (target || $('[data-close-dialog]', fresh))?.focus({ preventScroll: true });
    }
    return true;
  }

  async function refreshDrawer() {
    try {
      const url = new URL(location.href);
      url.searchParams.set('sections', CART_SECTION);
      url.searchParams.set('_cart_refresh', String(Date.now()));
      const response = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (response.ok) renderDrawer(await response.json());
    } catch (_) { /* network: leave current state */ }
  }

  function showCartError(message) {
    const error = $('[data-cart-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }

  function setBusy(element, busy) {
    if (!element) return;
    element.setAttribute('aria-busy', busy ? 'true' : 'false');
    if (element.matches('button')) element.disabled = busy || element.dataset.soldOut === 'true';
  }

  async function addToCart(form, submitter) {
    const status = $('[data-form-status]', form);
    const data = new FormData(form);
    if (!data.get('id')) {
      if (status) status.textContent = 'Choose a size first.';
      return;
    }
    data.append('sections', CART_SECTION);
    data.append('sections_url', location.pathname);
    setBusy(submitter, true);
    try {
      if (!await capAddToLiveStock(form, data)) return;
      const response = await fetch(`${routes.cartAdd || '/cart/add'}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: data
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.status) throw new Error(result.description || result.message || strings.error);
      if (!renderDrawer(result.sections)) await refreshDrawer();
      showCartError('');
      const dialog = doc.getElementById('cart-dialog');
      if (dialog && !dialog.open) openPanel(dialog, submitter);
      announce(`${result.product_title || result.title || ''} — ${strings.added || 'Added to your bag'}`);
    } catch (error) {
      if (status) status.textContent = error.message || strings.error;
      announce(error.message || strings.error);
    } finally {
      setBusy(submitter, false);
    }
  }

  let changeQueue = Promise.resolve();
  function changeLine(key, quantity) {
    changeQueue = changeQueue.then(async () => {
      const drawer = $('[data-cart-drawer]');
      drawer?.setAttribute('aria-busy', 'true');
      try {
        const line = $$('[data-cart-line]').find((item) => item.dataset.cartKey === String(key));
        if (quantity > 0 && line) {
          const cart = await getCartSnapshot();
          const limit = await cartLineStockLimit(line, cart);
          if (limit !== null && quantity > limit) {
            quantity = limit;
            showCartError(`Only ${limit} of this item are currently available. Your bag has been adjusted.`);
          }
        }
        const response = await fetch(`${routes.cartChange || '/cart/change'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity, sections: [CART_SECTION], sections_url: location.pathname })
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || result.status) throw new Error(result.description || result.message || strings.error);
        renderDrawer(result.sections) || await refreshDrawer();
        announce(quantity === 0 ? 'Removed from your bag' : 'Bag updated');
      } catch (error) {
        await refreshDrawer();
        showCartError(error.message || strings.error);
        announce(error.message || strings.error);
      } finally {
        $('[data-cart-drawer]')?.removeAttribute('aria-busy');
      }
    });
    return changeQueue;
  }

  function initCart() {
    doc.addEventListener('submit', (event) => {
      const form = event.target.closest('form[data-product-form]');
      if (!form || !window.fetch || !window.FormData) return;
      event.preventDefault();
      const submitter = event.submitter || $('[type="submit"]', form);
      if (submitter && submitter.disabled) return;
      addToCart(form, submitter);
    });
    doc.addEventListener('click', (event) => {
      const button = event.target.closest('[data-cart-change]');
      if (!button) return;
      event.preventDefault();
      changeLine(button.dataset.cartChange, Math.max(0, parseInt(button.dataset.quantity, 10) || 0));
    });
    doc.addEventListener('change', (event) => {
      const input = event.target.closest('[data-cart-input]');
      if (!input) return;
      changeLine(input.dataset.cartInput, Math.max(0, parseInt(input.value, 10) || 0));
    });
    let checkoutSubmitting = false;
    doc.addEventListener('submit', async (event) => {
      const form = event.target;
      const drawerCheckout = form.matches('form.drawer__checkout');
      const pageCheckout = form.matches('#CartPageForm') && event.submitter?.name === 'checkout';
      if ((!drawerCheckout && !pageCheckout) || checkoutSubmitting) return;
      event.preventDefault();
      checkoutSubmitting = true;
      const submitter = event.submitter;
      form.setAttribute('aria-busy', 'true');
      try {
        const adjusted = await synchronizeCartStock();
        if (adjusted) return;
        form.requestSubmit(submitter || undefined);
      } finally {
        checkoutSubmitting = false;
        form.removeAttribute('aria-busy');
      }
    });
    if ($('.cart-page')) synchronizeCartStock();
  }

  /* ------------------------------------------------------------------ Search */
  function initSearch() {
    const input = $('[data-predictive-search]');
    const output = $('[data-predictive-results]');
    const suggestions = $('[data-search-suggest]');
    if (!input || !output || !window.fetch) return;
    let timer = 0;
    let controller = null;

    const render = (results, query) => {
      const products = results.products || [];
      const collections = results.collections || [];
      if (!products.length && !collections.length) {
        output.innerHTML = `<p class="predictive__empty">${escapeHtml(strings.noResults)}</p>`;
        return;
      }
      const productMarkup = products.map((product) => {
        const image = product.featured_image?.url || product.image || '';
        const price = product.price !== undefined ? formatMoney(Math.round(parseFloat(product.price) * 100)) : '';
        return `<li class="predictive__item"><a href="${escapeHtml(product.url)}">${image ? `<img class="predictive__img" src="${escapeHtml(image)}${image.includes('?') ? '&' : '?'}width=160" alt="" width="64" height="64" loading="lazy">` : '<span></span>'}<span class="predictive__title">${escapeHtml(product.title)}</span><span class="predictive__price">${escapeHtml(price)}</span></a></li>`;
      }).join('');
      const collectionMarkup = collections.map((collection) => `<li><a href="${escapeHtml(collection.url)}">${escapeHtml(collection.title)} <svg class="icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M6 18 18 6M8 6h10v10"/></svg></a></li>`).join('');
      output.innerHTML = `
        ${products.length ? `<p class="micro" style="color:var(--muted);margin-bottom:12px">Products</p><ul class="predictive__list" role="list">${productMarkup}</ul>` : ''}
        ${collections.length ? `<p class="micro" style="color:var(--muted);margin:24px 0 8px">Collections</p><ul class="suggest-links" role="list">${collectionMarkup}</ul>` : ''}
        <p class="predictive__all"><a class="text-link" href="${escapeHtml(routes.search || '/search')}?q=${encodeURIComponent(query)}&amp;options%5Bprefix%5D=last">All results for “${escapeHtml(query)}”</a></p>`;
    };

    input.addEventListener('input', () => {
      clearTimeout(timer);
      const query = input.value.trim();
      if (query.length < 2) {
        controller?.abort();
        output.innerHTML = '';
        if (suggestions) suggestions.hidden = false;
        return;
      }
      timer = setTimeout(async () => {
        controller?.abort();
        controller = new AbortController();
        const params = new URLSearchParams({ q: query, 'resources[type]': 'product,collection', 'resources[limit]': '6', 'resources[options][unavailable_products]': 'last' });
        try {
          const response = await fetch(`${routes.predictiveSearch || '/search/suggest'}.json?${params}`, { signal: controller.signal, headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error(strings.error);
          const data = await response.json();
          if (suggestions) suggestions.hidden = true;
          render(data.resources?.results || {}, query);
        } catch (error) {
          if (error.name !== 'AbortError') output.innerHTML = `<p class="predictive__empty">${escapeHtml(strings.error)}</p>`;
        }
      }, 180);
    });
  }

  /* ---------------------------------------------------------------- Reveals */
  let revealObserver = null;
  function initReveals(scope = doc) {
    const items = $$('[data-reveal]:not(.is-inview)', scope);
    if (!('IntersectionObserver' in window) || reducedMotion.matches) {
      items.forEach((item) => item.classList.add('is-inview'));
      return;
    }
    revealObserver ||= new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-inview');
        revealObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    items.forEach((item) => revealObserver.observe(item));
  }

  function pauseWhenHidden(element) {
    if (!('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(([entry]) => element.classList.toggle('is-paused', !entry.isIntersecting));
    observer.observe(element);
    return observer;
  }

  /* -------------------------------------------------------------------- Hero */
  function initHero(scope = doc) {
    const stage = $('[data-stage]', scope);
    const ready = () => requestAnimationFrame(() => requestAnimationFrame(() => {
      root.classList.add('is-ready');
      setTimeout(() => root.classList.add('is-settled'), 2200);
    }));
    if (!stage) { ready(); return; }
    const images = $$('.herd__item.is-active .herd__img, .herd__img[loading="eager"]', stage);
    const decoded = Promise.all(images.map((image) => (image.decode ? image.decode().catch(() => {}) : Promise.resolve())));
    Promise.race([decoded, new Promise((resolve) => setTimeout(resolve, 1100))]).then(ready);
    stage.bulltongPauseObserver = pauseWhenHidden(stage);
    $$('[data-herd]', stage).forEach(initHerd);
    initStage(stage);
  }

  /* The herd: a coverflow of real packs. Drag, swipe, click, arrow keys, wheel. */
  function initHerd(herd) {
    if (herd.dataset.ready) return;
    herd.dataset.ready = 'true';
    const world = herd.closest('[data-stage]') || herd;
    const stage = $('[data-herd-stage]', herd);
    const items = $$('[data-herd-item]', herd);
    const dots = $$('[data-herd-dot]', herd);
    const nameEl = $('[data-herd-name]', herd);
    const heatEl = $('[data-herd-heat]', herd);
    const priceEl = $('[data-herd-price]', herd);
    const wordEl = $('[data-herd-word]', herd);
    const buyForms = $$('[data-herd-buy]', world);
    const count = items.length;
    if (!stage || count < 2) return;
    const life = new AbortController();
    const observers = [];

    const half = Math.floor(count / 2);
    const wrap = (value) => ((value % count) + count) % count;
    const offsetOf = (index, from) => { const o = wrap(index - from); return o > half ? o - count : o; };
    const inStory = () => world.classList.contains('is-story');
    let active = Math.max(0, items.findIndex((item) => item.classList.contains('is-active')));
    let switchTimer = 0;
    let dragFrame = 0;
    let autoplayFrame = 0;
    let dragValue = 0;
    const lights = $$('.stage__flood-light', world);
    let lit = 0;
    let hasLit = false;

    const paint = (drag = 0) => {
      items.forEach((item, index) => item.style.setProperty('--offset', (offsetOf(index, active) + drag).toFixed(3)));
    };

    const describe = () => {
      const item = items[active];
      world.style.setProperty('--active', item.dataset.accent);
      if (lights.length === 2) {
        if (!hasLit) {
          lights[0].style.setProperty('--flood-color', item.dataset.accent);
          lights[1].style.setProperty('--flood-color', item.dataset.accent);
          hasLit = true;
        } else {
          const next = 1 - lit;
          lights[next].style.setProperty('--flood-color', item.dataset.accent);
          lights[next].classList.add('is-current');
          lights[lit].classList.remove('is-current');
          lit = next;
        }
      }
      dots.forEach((dot, index) => dot.setAttribute('aria-current', index === active ? 'true' : 'false'));
      items.forEach((entry, index) => {
        entry.classList.toggle('is-active', index === active);
        entry.classList.toggle('is-near', Math.abs(offsetOf(index, active)) <= 1);
        const link = $('.herd__link', entry);
        if (link) link.dataset.cursor = index === active ? 'View' : 'Select';
      });
      buyForms.forEach((form, index) => { form.hidden = index !== active; });
      const front = $('.herd__face--front .herd__img', item);
      if (front) item.style.setProperty('--mask', `url("${front.currentSrc || front.src}")`);
      clearTimeout(switchTimer);
      herd.classList.add('is-switching');
      switchTimer = setTimeout(() => {
        if (nameEl) nameEl.textContent = item.dataset.name;
        if (heatEl) heatEl.textContent = item.dataset.heat;
        if (priceEl) priceEl.textContent = item.dataset.price;
        if (wordEl) { wordEl.textContent = item.dataset.word || ''; wordEl.style.setProperty('--chars', Math.max(4, (item.dataset.word || '').length)); }
        herd.classList.remove('is-switching');
      }, reducedMotion.matches ? 0 : 220);
      herd.dispatchEvent(new CustomEvent('herd:change', { bubbles: true, detail: { index: active, handle: item.dataset.handle } }));
    };

    const go = (target) => {
      const next = wrap(target);
      if (next === active) { paint(); return; }
      const before = items.map((_, index) => offsetOf(index, active));
      active = next;
      // Packs that wrap from one end of the arc to the other jump rather than sweep across the stage.
      items.forEach((item, index) => { if (Math.abs(offsetOf(index, active) - before[index]) > 1.5) item.classList.add('no-anim'); });
      paint();
      requestAnimationFrame(() => requestAnimationFrame(() => items.forEach((item) => item.classList.remove('no-anim'))));
      describe();
      restartAutoplay();
    };

    /* Autoplay follows the progress bar's own animation, so they never drift apart. */
    const delay = Number(herd.dataset.autoplay) || 0;
    const canAutoplay = delay > 0 && !reducedMotion.matches;
    const pauseReasons = new Set();
    const setPaused = (reason, paused) => {
      if (paused) pauseReasons.add(reason); else pauseReasons.delete(reason);
      herd.classList.toggle('is-paused', pauseReasons.size > 0);
    };
    function restartAutoplay() {
      if (!canAutoplay) return;
      if (autoplayFrame) cancelAnimationFrame(autoplayFrame);
      herd.classList.remove('is-autoplay');
      autoplayFrame = requestAnimationFrame(() => {
        autoplayFrame = requestAnimationFrame(() => {
          autoplayFrame = 0;
          herd.classList.add('is-autoplay');
        });
      });
    }
    if (canAutoplay) {
      herd.style.setProperty('--autoplay', `${delay}ms`);
      herd.classList.add('is-autoplay');
      herd.addEventListener('animationend', (event) => {
        if (event.animationName === 'progress' && event.target.closest('[data-herd-dot]') && !pauseReasons.size) go(active + 1);
      }, { signal: life.signal });
      if (finePointer.matches) {
        herd.addEventListener('pointerenter', () => setPaused('hover', true), { signal: life.signal });
        herd.addEventListener('pointerleave', () => setPaused('hover', false), { signal: life.signal });
      }
      herd.addEventListener('focusin', () => setPaused('focus', true), { signal: life.signal });
      herd.addEventListener('focusout', (event) => { if (!herd.contains(event.relatedTarget)) setPaused('focus', false); }, { signal: life.signal });
      doc.addEventListener('visibilitychange', () => setPaused('hidden', doc.hidden), { signal: life.signal });
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(([entry]) => setPaused('offscreen', !entry.isIntersecting));
        observer.observe(herd);
        observers.push(observer);
      }
    }

    /* Drag and swipe. Movement past 6px becomes a drag; otherwise it's a tap. */
    let pointer = null;
    let suppressClick = false;
    const gapPx = () => {
      const a = items[active].getBoundingClientRect();
      const b = items[wrap(active + 1)].getBoundingClientRect();
      return Math.max(60, Math.abs((b.left + b.width / 2) - (a.left + a.width / 2)));
    };
    stage.addEventListener('pointerdown', (event) => {
      if (event.button !== 0 || inStory()) return;
      pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, dx: 0, moved: false, gap: gapPx() };
      setPaused('drag', true);
    }, { signal: life.signal });
    stage.addEventListener('pointermove', (event) => {
      if (!pointer || event.pointerId !== pointer.id) return;
      pointer.dx = event.clientX - pointer.x;
      if (!pointer.moved) {
        if (Math.abs(pointer.dx) < 6) return;
        if (Math.abs(event.clientY - pointer.y) > Math.abs(pointer.dx)) { pointer = null; setPaused('drag', false); return; }
        pointer.moved = true;
        herd.classList.add('is-dragging');
        try { stage.setPointerCapture(event.pointerId); } catch (_) { /* older Safari */ }
      }
      dragValue = Math.max(-half, Math.min(half, pointer.dx / pointer.gap));
      if (!dragFrame) dragFrame = requestAnimationFrame(() => { dragFrame = 0; paint(dragValue); });
    }, { signal: life.signal, passive: true });
    const endDrag = (event) => {
      if (!pointer || (event && event.pointerId !== pointer.id)) return;
      const { moved, dx, gap } = pointer;
      pointer = null;
      if (dragFrame) { cancelAnimationFrame(dragFrame); dragFrame = 0; }
      herd.classList.remove('is-dragging');
      setPaused('drag', false);
      if (!moved) return;
      suppressClick = true;
      setTimeout(() => { suppressClick = false; }, 0);
      const steps = Math.abs(dx) > gap * .22 ? -Math.sign(dx) * Math.max(1, Math.round(Math.abs(dx) / gap)) : 0;
      go(active + steps);
    };
    stage.addEventListener('pointerup', endDrag, { signal: life.signal });
    stage.addEventListener('pointercancel', endDrag, { signal: life.signal });

    /* Click a side pack to bring it forward; click the front pack to open it. */
    stage.addEventListener('click', (event) => {
      if (suppressClick) { event.preventDefault(); event.stopPropagation(); return; }
      const item = event.target.closest('[data-herd-item]');
      if (!item) return;
      const index = items.indexOf(item);
      if (index !== active) { event.preventDefault(); go(index); }
    }, { capture: true, signal: life.signal });

    /* Keyboard + focus: tabbing through the packs turns the line-up with you. */
    herd.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      event.preventDefault();
      go(active + (event.key === 'ArrowRight' ? 1 : -1));
      $('.herd__link', items[active])?.focus({ preventScroll: true });
    }, { signal: life.signal });
    stage.addEventListener('focusin', (event) => {
      const index = items.indexOf(event.target.closest('[data-herd-item]'));
      if (index >= 0 && index !== active && !pointer) go(index);
    }, { signal: life.signal });

    /* Horizontal trackpad swipes. */
    let wheel = 0;
    let wheelLock = 0;
    stage.addEventListener('wheel', (event) => {
      if (inStory() || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      event.preventDefault();
      if (Date.now() < wheelLock) return;
      wheel += event.deltaX;
      if (Math.abs(wheel) > 50) { go(active + Math.sign(wheel)); wheel = 0; wheelLock = Date.now() + 450; }
    }, { passive: false, signal: life.signal });

    $('[data-herd-prev]', herd)?.addEventListener('click', () => go(active - 1), { signal: life.signal });
    $('[data-herd-next]', herd)?.addEventListener('click', () => go(active + 1), { signal: life.signal });
    dots.forEach((dot, index) => dot.addEventListener('click', () => go(index), { signal: life.signal }));
    herd.addEventListener('shopify:block:select', (event) => {
      const index = items.indexOf(event.target.closest('[data-herd-item]'));
      if (index >= 0) go(index);
      setPaused('editor', true);
    }, { signal: life.signal });
    herd.addEventListener('shopify:block:deselect', () => setPaused('editor', false), { signal: life.signal });

    herd.bulltongHerd = { go, setPaused, get active() { return active; } };
    herd.bulltongDispose = () => {
      life.abort();
      observers.forEach((observer) => observer.disconnect());
      clearTimeout(switchTimer);
      if (dragFrame) cancelAnimationFrame(dragFrame);
      if (autoplayFrame) cancelAnimationFrame(autoplayFrame);
      delete herd.dataset.ready;
    };
    paint();
    describe();
    herd.classList.add('is-intro');
    setTimeout(() => herd.classList.remove('is-intro'), 2400);
  }

  /* The stage: scroll progress (native scrolling) becomes one continuous camera move. */
  function initStage(stage) {
    if (stage.dataset.stageReady) return;
    stage.dataset.stageReady = 'true';
    const scroller = $('[data-stage-scroller]', stage);
    const herd = $('[data-herd]', stage);
    const panels = $$('[data-stage-panel]', stage);
    const intros = $$('[data-stage-intro]', stage);
    const chips = $$('[data-stage-flavour]', stage);
    const rail = $$('[data-stage-goto]', stage);
    if (!scroller || !herd) return;
    const life = new AbortController();
    const observers = [];

    const chapters = Math.max(1, panels.length);
    const EXPAND = [0.03, 0.2];
    const COPY = [0.16, 0.25];
    const STORY_START = 0.24;
    const STORY_END = 1.001;
    const span = (STORY_END - STORY_START) / chapters;
    const smoothstep = (x, a, b) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
    let target = 0;
    let current = 0;
    let frame = 0;
    let last = 0;
    let chapter = -1;
    let story = false;
    let visible = true;
    let startY = 0;
    let scrollRange = 1;
    let resizeFrame = 0;
    let lastExpand = '';
    let lastCopy = '';
    let lastFace = stage.dataset.face;
    let poseTimer = 0;
    // Keep per-frame variables off the stage root. The article text, navigation,
    // and other descendants no longer need style recalculation for every tick.
    const expandTargets = [
      $('.stage__flood', stage), $('.stage__beam', stage),
      $('.stage__intro', stage), herd, $('.stage__foot', stage)
    ].filter(Boolean);
    const copyTargets = [$('.stage__copy', stage), $('.stage__rail', stage)].filter(Boolean);

    const beginPoseTransition = () => {
      if (reducedMotion.matches) return;
      clearTimeout(poseTimer);
      stage.classList.add('is-pose-transitioning');
      poseTimer = setTimeout(() => stage.classList.remove('is-pose-transitioning'), 1400);
    };
    const setChapter = (index) => {
      if (chapter >= 0) beginPoseTransition();
      chapter = index;
      stage.dataset.chapter = String(index);
      panels.forEach((panel, n) => panel.classList.toggle('is-active', n === index));
      rail.forEach((button, n) => button.setAttribute('aria-current', n === index ? 'true' : 'false'));
    };

    const apply = (progress) => {
      const expand = smoothstep(progress, EXPAND[0], EXPAND[1]);
      const copy = smoothstep(progress, COPY[0], COPY[1]);
      const expandValue = expand.toFixed(4);
      const copyValue = copy.toFixed(4);
      if (expandValue !== lastExpand) {
        expandTargets.forEach((element) => element.style.setProperty('--expand', expandValue));
        lastExpand = expandValue;
      }
      if (copyValue !== lastCopy) {
        copyTargets.forEach((element) => element.style.setProperty('--copy', copyValue));
        lastCopy = copyValue;
      }
      const nextStory = expand > .5;
      if (nextStory !== story) {
        story = nextStory;
        stage.classList.toggle('is-story', story);
        herd.dataset.cursor = story ? '' : 'Drag';
        herd.bulltongHerd?.setPaused('story', story);
      }
      const nextChapter = progress < STORY_START ? 0 : Math.min(chapters - 1, Math.floor((progress - STORY_START) / span));
      if (nextChapter !== chapter) setChapter(nextChapter);
      const face = story && panels[chapter]?.dataset.face === 'back' ? 'back' : 'front';
      if (face !== lastFace) { stage.dataset.face = face; lastFace = face; }
    };
    // Read the section's geometry only when it can change. A raw scroll event
    // now touches no layout properties, even on high-frequency touch devices.
    const refreshBounds = () => {
      const rect = scroller.getBoundingClientRect();
      startY = window.scrollY + rect.top;
      scrollRange = Math.max(1, rect.height - window.innerHeight);
    };
    const progressAtScroll = () => Math.min(1, Math.max(0, (window.scrollY - startY) / scrollRange));
    // Time-based easing toward the scroll position: identical feel at 60Hz or 120Hz.
    const tick = (now) => {
      if (!visible || doc.hidden) { frame = 0; last = 0; return; }
      const dt = last ? Math.min(64, now - last) : 16;
      last = now;
      const follow = reducedMotion.matches ? 1 : 1 - Math.exp(-dt / 85);
      current += (target - current) * follow;
      if (Math.abs(target - current) < 0.0004) current = target;
      apply(current);
      if (current !== target) frame = requestAnimationFrame(tick);
      else { frame = 0; last = 0; }
    };
    const onScroll = () => {
      target = progressAtScroll();
      if (visible && !doc.hidden && !frame) frame = requestAnimationFrame(tick);
    };
    const scheduleBounds = () => {
      if (resizeFrame) return;
      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = 0;
        refreshBounds();
        onScroll();
      });
    };
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(([entry]) => {
        visible = entry.isIntersecting;
        if (visible) { refreshBounds(); onScroll(); }
        else if (frame) { cancelAnimationFrame(frame); frame = 0; last = 0; }
      }, { rootMargin: '25% 0px' });
      observer.observe(scroller);
      observers.push(observer);
    }
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(scheduleBounds);
      observer.observe(scroller);
      observers.push(observer);
    }
    window.addEventListener('scroll', onScroll, { passive: true, signal: life.signal });
    window.addEventListener('resize', scheduleBounds, { passive: true, signal: life.signal });
    window.addEventListener('pageshow', scheduleBounds, { signal: life.signal });
    doc.addEventListener('visibilitychange', () => {
      stage.classList.toggle('is-paused', doc.hidden);
      if (doc.hidden && frame) { cancelAnimationFrame(frame); frame = 0; last = 0; }
      else if (!doc.hidden) onScroll();
    }, { signal: life.signal });

    const scrollToProgress = (progress) => {
      const top = startY + progress * scrollRange;
      window.scrollTo({ top, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    };
    rail.forEach((button, index) => button.addEventListener('click', () => {
      scrollToProgress(index === 0 ? STORY_START + span * .35 : STORY_START + (index + .5) * span);
    }, { signal: life.signal }));

    herd.addEventListener('herd:change', (event) => {
      if (story) beginPoseTransition();
      const index = event.detail.index;
      intros.forEach((intro, n) => intro.classList.toggle('is-active', n === index));
      chips.forEach((chip, n) => chip.setAttribute('aria-current', n === index ? 'true' : 'false'));
    }, { signal: life.signal });
    chips.forEach((chip, index) => chip.addEventListener('click', () => herd.bulltongHerd?.go(index), { signal: life.signal }));

    doc.addEventListener('click', (event) => {
      const link = event.target.closest('a[href^="#"]');
      if (!link || link.getAttribute('href').length < 2) return;
      const destination = stage.querySelector(link.getAttribute('href').replace(/[^#\w-]/g, ''));
      if (!destination) return;
      event.preventDefault();
      scrollToProgress(STORY_START + span * .35);
    }, { signal: life.signal });
    refreshBounds();
    target = progressAtScroll();
    current = target;
    apply(current);
    // Deep links such as #flavours land on the story rather than the top of the stage.
    if (location.hash && stage.querySelector(location.hash.replace(/[^#\w-]/g, ''))) scrollToProgress(STORY_START + span * .35);
    stage.bulltongDispose = () => {
      life.abort();
      observers.forEach((observer) => observer.disconnect());
      stage.bulltongPauseObserver?.disconnect();
      herd.bulltongDispose?.();
      if (frame) cancelAnimationFrame(frame);
      if (resizeFrame) cancelAnimationFrame(resizeFrame);
      clearTimeout(poseTimer);
      delete stage.dataset.stageReady;
    };
  }

  /* Desktop cursor label: DRAG / VIEW / SHOP beside the normal pointer. */
  function initCursorLabel() {
    if (!finePointer.matches || reducedMotion.matches) return;
    const tag = doc.createElement('div');
    tag.className = 'cursor-tag';
    tag.setAttribute('aria-hidden', 'true');
    doc.body.append(tag);
    let x = 0;
    let y = 0;
    let frame = 0;
    let label = '';
    doc.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'mouse') return;
      x = event.clientX;
      y = event.clientY;
      const owner = event.target.closest ? event.target.closest('[data-cursor]') : null;
      const next = owner ? owner.dataset.cursor : '';
      if (next !== label) {
        label = next;
        if (next) tag.textContent = next;
        tag.classList.toggle('is-on', Boolean(next));
      }
      if (!frame) frame = requestAnimationFrame(() => { frame = 0; tag.style.transform = `translate3d(${x}px, ${y}px, 0)`; });
    }, { passive: true });
    doc.documentElement.addEventListener('pointerleave', () => { label = ''; tag.classList.remove('is-on'); });
  }

  /* --------------------------------------------------------------- Carousels */
  function scrollToSlide(track, index) {
    track.scrollTo({ left: index * track.clientWidth, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  }

  function watchSlides(track, slides, onChange) {
    if (!('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) onChange(slides.indexOf(entry.target));
      });
    }, { root: track, threshold: [0.6] });
    slides.forEach((slide) => observer.observe(slide));
  }

  function initReviews(section) {
    if (section.dataset.ready) return;
    section.dataset.ready = 'true';
    const track = $('[data-reviews-track]', section);
    const slides = $$('[data-review]', section);
    const dots = $$('[data-review-dot]', section);
    if (!track || slides.length < 2) return;
    let index = 0;
    const setActive = (value) => {
      if (value < 0) return;
      index = value;
      slides.forEach((slide, n) => slide.classList.toggle('is-active', n === index));
      dots.forEach((dot, n) => dot.setAttribute('aria-current', n === index ? 'true' : 'false'));
    };
    dots.forEach((dot, n) => dot.addEventListener('click', () => scrollToSlide(track, n)));
    watchSlides(track, slides, setActive);

    const delay = Number(section.dataset.autoplay) || 0;
    if (delay && finePointer.matches && !reducedMotion.matches) {
      section.classList.add('is-autoplay');
      section.style.setProperty('--autoplay', `${delay}ms`);
      section.addEventListener('animationend', (event) => {
        if (event.animationName !== 'progress') return;
        scrollToSlide(track, (index + 1) % slides.length);
      });
      const pause = (paused) => section.classList.toggle('is-paused', paused);
      section.addEventListener('pointerenter', () => pause(true));
      section.addEventListener('pointerleave', () => pause(false));
      section.addEventListener('focusin', () => pause(true));
      section.addEventListener('focusout', () => pause(false));
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([entry]) => pause(!entry.isIntersecting)).observe(section);
      }
    }
  }

  /* The line-up returns: select to shop; follows the flavour chosen in the stage. */
  function initRange(section) {
    if (section.dataset.ready) return;
    section.dataset.ready = 'true';
    const items = $$('[data-range-item]', section);
    const panels = $$('[data-range-panel]', section);
    const select = (index) => {
      items.forEach((item, n) => {
        item.classList.toggle('is-selected', n === index);
        $('.range__pick', item)?.setAttribute('aria-pressed', n === index ? 'true' : 'false');
      });
      panels.forEach((panel, n) => panel.classList.toggle('is-active', n === index));
    };
    items.forEach((item, index) => $('.range__pick', item)?.addEventListener('click', () => select(index)));
    doc.addEventListener('herd:change', (event) => {
      const index = items.findIndex((item) => item.dataset.handle === event.detail.handle);
      if (index >= 0) select(index);
    });
  }

  /* --------------------------------------------------------------- Accordion */
  function initAccordions(scope = doc) {
    $$('[data-accordion-button]', scope).forEach((button) => {
      if (button.dataset.ready) return;
      button.dataset.ready = 'true';
      const panel = doc.getElementById(button.getAttribute('aria-controls'));
      const item = button.closest('.faq__item');
      if (panel) panel.inert = true;
      button.addEventListener('click', () => {
        const open = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', String(open));
        item?.classList.toggle('is-open', open);
        if (panel) panel.inert = !open;
      });
    });
  }

  /* ----------------------------------------------------------- Small helpers */
  function initControls() {
    doc.addEventListener('click', (event) => {
      const step = event.target.closest('[data-qty-step]');
      if (!step) return;
      const input = $('input', step.closest('.qty'));
      if (!input) return;
      const increment = Number(input.step) || 1;
      const min = input.min !== '' ? Number(input.min) : 0;
      const max = input.max !== '' ? Number(input.max) : Infinity;
      const value = (parseInt(input.value, 10) || 0) + increment * Number(step.dataset.qtyStep);
      input.value = Math.min(max, Math.max(min, value));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    doc.addEventListener('change', (event) => {
      const radio = event.target.closest('[data-variant-radio]');
      if (radio) {
        const price = $('[data-card-price]', radio.closest('[data-card]'));
        if (price) {
          price.innerHTML = radio.dataset.compare
            ? `<span class="price price--sale"><span class="price__now">${escapeHtml(radio.dataset.price)}</span><span class="visually-hidden">Regular price</span><s class="price__was">${escapeHtml(radio.dataset.compare)}</s></span>`
            : `<span class="price"><span class="price__now">${escapeHtml(radio.dataset.price)}</span></span>`;
        }
      }
      const autoSubmit = event.target.closest('[data-auto-submit]');
      if (autoSubmit && autoSubmit.form) autoSubmit.form.requestSubmit ? autoSubmit.form.requestSubmit() : autoSubmit.form.submit();
    });
  }

  /* ------------------------------------------------------------------ Boot */
  function initScope(scope = doc) {
    $$('[data-reviews]', scope).forEach(initReviews);
    $$('[data-range]', scope).forEach(initRange);
    initAccordions(scope);
    initReveals(scope);
  }

  initHeader();
  initPanels();
  initCart();
  initSearch();
  initControls();
  initHero();
  initCursorLabel();
  initScope();

  doc.addEventListener('shopify:section:load', (event) => {
    initScope(event.target);
    if ($('[data-stage]', event.target)) initHero(event.target);
  });
  doc.addEventListener('shopify:section:unload', (event) => {
    const stage = event.target.matches?.('[data-stage]') ? event.target : $('[data-stage]', event.target);
    stage?.bulltongDispose?.();
  });
})();
